import { AxiosResponse } from 'axios';
import { gzipSync, strToU8 } from 'fflate';
import { KEEPALIVE_LIMIT_BYTES } from '@srm/core/http';
import $api, { sendKeepalive } from '@/http/index';
import { IDocumentDraftData } from '@models/documentDraft/IDocumentDraftData';
import { IDocumentDraft } from '@models/documentDraft/IDocumentDraft';

/** Ответ GET /DocumentDraft/{id}: черновик с данными либо надгробие. */
export type DocumentDraftLookup =
    { kind: 'draft'; draft: IDocumentDraftData } | { kind: 'consumed'; docId: string } | { kind: 'missing' };

/** Сервер отверг PUT: версия на нём новее присланной (правили в другой вкладке). */
export class DraftConflictError extends Error {
    constructor(
        readonly serverVersion: number | null,
        readonly updatedAt: string | null,
    ) {
        super('Черновик изменён в другой вкладке');
        this.name = 'DraftConflictError';
    }
}

/**
 * PUT получил 404: у текущего пользователя такого черновика нет, а id занят чужой записью
 * (открыли ссылку на черновик коллеги или сменили учётку во вкладке). Эти правки сохраняются
 * только под новым id.
 */
export class DraftNotFoundError extends Error {
    constructor() {
        super('Черновик с этим адресом принадлежит другому пользователю');
        this.name = 'DraftNotFoundError';
    }
}

/** Сервер окончательно отверг запись (4xx): повтор тех же данных получит тот же ответ. */
export class DraftRejectedError extends Error {
    constructor(readonly status: number) {
        super(`Сервер не принял черновик (код ${status})`);
        this.name = 'DraftRejectedError';
    }
}

// Ожидаемые отказы (404/409/410) получаем как ответ, а не как ошибку: интерсептор $api
// разворачивает тело любого 4xx в строку-сообщение, и по ней статус уже не различить.
const accept =
    (...statuses: number[]) =>
    (status: number) =>
        (status >= 200 && status < 300) || statuses.includes(status);

// 401 должен дойти до интерсептора (обновление токена и повтор), 408/429 — временные отказы.
const RETRYABLE_CLIENT_ERRORS = new Set([401, 408, 429]);
const acceptClientErrors = (status: number) =>
    (status >= 200 && status < 300) || (status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERRORS.has(status));

// Тело записи — сжатый JSON (сервер распаковывает по Content-Encoding): ключи строк таблиц и связей повторяются в
// каждой строке, и черновик ужимается на порядок — в том числе под лимит keepalive при уходе со страницы. Автосейв
// сжимает нативным потоком, не занимая главный поток; keepalive уходит из pagehide, где ждать нельзя, — синхронно.
const gzipBlob = (value: unknown): Promise<Blob> =>
    new Response(new Response(JSON.stringify(value)).body!.pipeThrough(new CompressionStream('gzip'))).blob();
const gzipJson = (value: unknown) => gzipSync(strToU8(JSON.stringify(value)));
const GZIP_HEADERS = { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip' };
const ifMatch = (version?: number): Record<string, string> => (version ? { 'If-Match': String(version) } : {});

export class DocumentDraftService {
    /** Мои черновики без надгробий, новейшие первыми; actionId — только по действию создания. */
    static async list(actionId?: string): Promise<IDocumentDraft[]> {
        const response = await $api.get<IDocumentDraft[]>('/DocumentDraft', {
            params: actionId ? { actionId } : undefined,
        });
        return response.data ?? [];
    }

    /** Черновик с данными. 404 → missing, 410 → consumed (черновик стал документом). */
    static async get(id: string): Promise<DocumentDraftLookup> {
        const response = await $api.get<IDocumentDraftData>(`/DocumentDraft/${id}`, {
            suppressErrorNotification: true,
            validateStatus: accept(404, 410),
        });
        if (response.status === 404) return { kind: 'missing' };
        if (response.status === 410) {
            const docId = (response.data as IDocumentDraft | undefined)?.consumedDocId;
            return docId ? { kind: 'consumed', docId } : { kind: 'missing' };
        }
        return response.data ? { kind: 'draft', draft: response.data } : { kind: 'missing' };
    }

    /**
     * Upsert по id. expectedVersion — версия, от которой правили (If-Match); расхождение → DraftConflictError.
     * 404 → DraftNotFoundError, прочие окончательные 4xx (413 — черновик больше предела сервера) → DraftRejectedError;
     * сеть и 5xx — исходная ошибка.
     * Имя черновика здесь не меняется — только rename().
     */
    static async put(draft: IDocumentDraftData, expectedVersion?: number): Promise<IDocumentDraftData> {
        const body = await gzipBlob(draft);
        const response: AxiosResponse<IDocumentDraftData> = await $api.put(`/DocumentDraft/${draft.id}`, body, {
            headers: { ...GZIP_HEADERS, ...ifMatch(expectedVersion) },
            suppressErrorNotification: true,
            validateStatus: acceptClientErrors,
        });
        if (response.status === 409) {
            const current = response.data as IDocumentDraft | undefined;
            throw new DraftConflictError(current?.version ?? null, current?.updatedAt ?? null);
        }
        if (response.status === 404) throw new DraftNotFoundError();
        if (response.status >= 400) throw new DraftRejectedError(response.status);
        return response.data;
    }

    /** Имя черновика; пустое — снять имя. */
    static async rename(id: string, name: string | null): Promise<void> {
        // Отказ показывают вызывающие (модалка), общий тост интерсептора его бы дублировал.
        await $api.patch(`/DocumentDraft/${id}`, { name: name?.trim() || null }, { suppressErrorNotification: true });
    }

    /** Удаление; 404 (уже удалён или чужой) — не ошибка: результат тот же. */
    static async remove(id: string): Promise<void> {
        await $api.delete(`/DocumentDraft/${id}`, { suppressErrorNotification: true, validateStatus: accept(404) });
    }

    /**
     * Отправка черновика при уходе со страницы (pagehide): обычный запрос вкладка оборвёт, keepalive —
     * доживёт (`sendKeepalive`). false — тело не укладывается в лимит keepalive, отправлять бесполезно.
     */
    static putKeepalive(draft: IDocumentDraftData, expectedVersion?: number): boolean {
        return sendKeepalive('PUT', `/DocumentDraft/${draft.id}`, gzipJson(draft), {
            'Content-Encoding': 'gzip',
            ...ifMatch(expectedVersion),
        });
    }

    /** Уложится ли черновик в лимит {@link putKeepalive}; несжатый в него влез — сжимать незачем. */
    static fitsKeepalive(draft: IDocumentDraftData): boolean {
        const json = strToU8(JSON.stringify(draft));
        return json.byteLength <= KEEPALIVE_LIMIT_BYTES || gzipSync(json).byteLength <= KEEPALIVE_LIMIT_BYTES;
    }
}
